import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { take } from 'rxjs/operators';
import { JiraService, JiraConnection, JiraDefaults, JiraSite } from '../../../core/services/jira.service';
import { GitHubService, GitHubConnection, GitHubDefaults } from '../../../core/services/github.service';
import { AwsService, AwsConnection } from '../../../core/services/aws.service';
import { AzureService, AzureConnection, AzureSubscription } from '../../../core/services/azure.service';
import { GcpService, GcpConnection, GcpProject } from '../../../core/services/gcp.service';
import { CloudService } from '../../../core/services/cloud.service';
import { SUPPORTED_AWS_REGIONS } from '../../../core/aws-regions';
import { SUPPORTED_AZURE_LOCATIONS, locationLabel } from '../../../core/azure-locations';
import { SUPPORTED_GCP_REGIONS, regionLabel } from '../../../core/gcp-regions';
import { OrgService, MemberWithUser } from '../../../core/services/org.service';
import { NotificationService, EmailChannelState } from '../../../core/services/notification.service';
import { ToastService } from '../../../core/services/toast.service';

type JiraPageState   = 'loading' | 'not-connected' | 'pending-site' | 'connected' | 'needs-reauth' | 'error';
type GitHubPageState = 'loading' | 'not-connected' | 'connected' | 'error';
type AwsPageState    = 'loading' | 'not-connected' | 'testing' | 'connected' | 'error';
type AzurePageState  = 'loading' | 'not-connected' | 'testing' | 'connected' | 'error';
type GcpPageState    = 'loading' | 'not-connected' | 'testing' | 'connected' | 'error';

export type IntegrationCategory = 'cloud' | 'ticketing' | 'notifications';

interface ComingSoonIntegration {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: IntegrationCategory;
  iconBg: string;
  iconColor: string;
}

// Azure is now a first-class connect card (below); no cloud providers are
// "coming soon" any more.
const COMING_SOON: ComingSoonIntegration[] = [];

@Component({
  selector: 'app-integration-settings',
  templateUrl: './integration-settings.component.html',
})
export class IntegrationSettingsComponent implements OnInit {

  // ── Jira ──────────────────────────────────────────────────────────────────

  jiraState: JiraPageState = 'loading';
  jiraConnection: JiraConnection | null = null;

  // Banners set by the Jira OAuth callback redirect (?connected=true / ?error=...)
  jiraConnectSuccess = false;
  jiraConnectError: string | null = null;

  pendingSites: JiraSite[] = [];
  selectingCloudId: string | null = null;
  siteSelectError: string | null = null;

  jiraConnectLoading    = false;
  jiraDisconnectLoading = false;

  // Default project key config (NOTIF#JIRA)
  jiraDefaults: JiraDefaults | null = null;
  jiraDefaultDraft    = '';
  jiraDefaultSaving   = false;
  jiraDefaultError: string | null = null;
  jiraDefaultSuccess  = false;

  // ── GitHub ────────────────────────────────────────────────────────────────

  githubState: GitHubPageState = 'loading';
  githubConnection: GitHubConnection | null = null;

  // Banners set by the GitHub OAuth callback redirect (?github_connected=true / ?github_error=...)
  githubConnectSuccess = false;
  githubConnectError: string | null = null;

  githubConnectLoading    = false;
  githubDisconnectLoading = false;

  // Default repository config (NOTIF#GITHUB)
  githubDefaults: GitHubDefaults | null = null;
  githubDefaultDraft = '';
  githubDefaultSaving   = false;
  githubDefaultError: string | null = null;
  githubDefaultSuccess  = false;

  // ── AWS ───────────────────────────────────────────────────────────────────

  awsState: AwsPageState = 'loading';
  awsConnection: AwsConnection | null = null;
  /** When true, the connect form is shown to add an additional AWS account. */
  awsAddingAccount = false;

  // Form inputs
  awsRoleArn = '';
  awsRegion  = 'us-east-1';   // region used to verify the role on connect

  // ── Add-additional-account form (separate state so a failed add never
  //    destabilises the already-connected view) ──────────────────────────────
  awsAddRoleArn     = '';
  awsAddRegion      = 'us-east-1';
  awsAddDisplayName = '';
  awsAddLoading     = false;
  awsAddError: string | null = null;

  // ── Per-account management (disconnect + per-account region editing) ─────────
  /** Account pending a disconnect confirmation, or null. */
  awsConfirmDisconnect: { accountId: string; label?: string; isPrimary: boolean } | null = null;
  awsDisconnectAccountLoading = false;
  /** Account whose region editor is currently expanded, or null. */
  awsEditRegionsAccountId: string | null = null;
  awsAcctDefaultRegion  = 'us-east-1';
  awsAcctEnabledRegions: string[] = ['us-east-1'];
  awsAcctRegionsSaving  = false;
  awsAcctRegionsError: string | null = null;

  /**
   * Connected AWS accounts for this subscription. Uses the backend `accounts`
   * array when present, otherwise the single account derived from the
   * connection. Drives the "AWS accounts" summary on this page.
   */
  get awsAccounts(): { accountId: string; label?: string; regionCount: number; isPrimary: boolean; defaultRegion: string; enabledRegions: string[] }[] {
    const conn = this.awsConnection;
    if (!conn || conn.connectionStatus !== 'ACTIVE') return [];
    if (conn.accounts && conn.accounts.length) {
      return conn.accounts.map(a => ({
        accountId: a.accountId,
        label: a.label,
        regionCount: a.enabledRegions.length,
        isPrimary: a.isPrimary ?? false,
        defaultRegion: a.defaultRegion,
        enabledRegions: a.enabledRegions,
      }));
    }
    if (!conn.accountId) return [];
    return [{
      accountId: conn.accountId,
      regionCount: this.awsEnabledRegions.length,
      isPrimary: true,
      defaultRegion: this.awsDefaultRegion,
      enabledRegions: this.awsEnabledRegions,
    }];
  }

  /** True when more than one AWS account is connected (drives per-account affordances). */
  get hasMultipleAwsAccounts(): boolean {
    return this.awsAccounts.length > 1;
  }

  /**
   * Stable identity for the AWS accounts *ngFor. `awsAccounts` is a getter that
   * builds a fresh array on every change-detection pass, so without trackBy the
   * per-account region editor (an ngModel-bearing <select>) would be destroyed
   * and recreated every tick — re-arming NgModel's init and looping CD forever.
   */
  trackByAwsAccountId(_index: number, a: { accountId: string }): string {
    return a.accountId;
  }

  startAddAwsAccount(): void {
    this.awsAddingAccount  = true;
    this.awsAddRoleArn     = '';
    this.awsAddRegion      = 'us-east-1';
    this.awsAddDisplayName = '';
    this.awsAddError       = null;
  }

  cancelAddAwsAccount(): void {
    this.awsAddingAccount = false;
    this.awsAddError      = null;
  }

  /**
   * Verifies and adds an ADDITIONAL AWS account. Deliberately isolated from
   * `testConnectAws`/`awsState`: a failure here surfaces an inline error and
   * leaves every already-connected account untouched. On success the
   * integrations view (and its account list) is refreshed from the backend.
   */
  addAwsAccount(): void {
    const roleArn = this.awsAddRoleArn.trim();
    if (!roleArn) { this.awsAddError = 'Enter the cross-account role ARN for the account to add.'; return; }

    this.awsAddLoading = true;
    this.awsAddError   = null;

    this.aws.testConnection(roleArn, this.awsAddRegion, this.awsAddDisplayName.trim() || undefined).subscribe({
      next: (result) => {
        this.awsAddLoading = false;
        if (result.success) {
          this.awsAddingAccount = false;
          this.toast.show(
            result.alreadyConnected
              ? `AWS account ${result.accountId} is already connected — its details were refreshed.`
              : `AWS account ${result.accountId} connected.`,
            'success',
          );
          // Refresh the integrations view and notify the Cloud page so its
          // account/region selectors pick up the new account without a reload.
          this.loadAwsConnection();
          this.cloud.refreshConnection();
        } else {
          // Non-destructive: stay on the form, show the reason, keep existing accounts intact.
          this.awsAddError = result.message;
        }
      },
      error: (err: { error?: { message?: string } }) => {
        this.awsAddLoading = false;
        this.awsAddError   = err?.error?.message ?? 'Failed to add AWS account. Please try again.';
      },
    });
  }

  // ── Disconnect a single account ─────────────────────────────────────────────

  /** Opens the disconnect confirmation for an account (read-only members blocked). */
  askDisconnectAccount(account: { accountId: string; label?: string; isPrimary: boolean }): void {
    if (this.isReadOnly) return;
    this.awsConfirmDisconnect = { accountId: account.accountId, label: account.label, isPrimary: account.isPrimary };
  }

  cancelDisconnectAccount(): void {
    this.awsConfirmDisconnect = null;
  }

  /** Confirms and performs the disconnect. Non-destructive to the other accounts. */
  confirmDisconnectAccount(): void {
    if (this.isReadOnly || !this.awsConfirmDisconnect) return;
    if (this.awsDisconnectAccountLoading) return; // guard against double-submit while in flight
    const { accountId } = this.awsConfirmDisconnect;
    this.awsDisconnectAccountLoading = true;
    this.aws.deleteAccount(accountId).subscribe({
      next: () => {
        this.awsDisconnectAccountLoading = false;
        this.awsConfirmDisconnect = null;
        if (this.awsEditRegionsAccountId === accountId) this.awsEditRegionsAccountId = null;
        this.toast.show(`AWS account ${accountId} disconnected.`, 'success');
        this.loadAwsConnection();
        this.cloud.refreshConnection();
      },
      error: (err: { error?: { message?: string } }) => {
        this.awsDisconnectAccountLoading = false;
        this.awsConfirmDisconnect = null;
        this.toast.show(err?.error?.message ?? 'Failed to disconnect the AWS account.', 'error');
      },
    });
  }

  // ── Per-account region editing ──────────────────────────────────────────────

  /** Expands the region editor for one account, seeded with its current regions. */
  startEditAccountRegions(account: { accountId: string; defaultRegion: string; enabledRegions: string[] }): void {
    if (this.isReadOnly) return;
    this.awsEditRegionsAccountId = account.accountId;
    this.awsAcctEnabledRegions   = [...account.enabledRegions];
    this.awsAcctDefaultRegion    = account.defaultRegion;
    this.awsAcctRegionsError     = null;
  }

  cancelEditAccountRegions(): void {
    this.awsEditRegionsAccountId = null;
    this.awsAcctRegionsError = null;
  }

  /** Toggles a region for the account being edited (the default stays enabled). */
  toggleAccountRegion(code: string): void {
    if (this.awsAcctEnabledRegions.includes(code)) {
      if (code === this.awsAcctDefaultRegion) return; // can't disable the default
      this.awsAcctEnabledRegions = this.awsAcctEnabledRegions.filter(r => r !== code);
    } else {
      this.awsAcctEnabledRegions = [...this.awsAcctEnabledRegions, code];
    }
    this.awsAcctRegionsError = null;
  }

  isAccountRegionEnabled(code: string): boolean {
    return this.awsAcctEnabledRegions.includes(code);
  }

  /** Persists the regions for ONE account; leaves the others untouched. */
  saveAccountRegions(): void {
    if (this.isReadOnly || !this.awsEditRegionsAccountId) return;
    if (this.awsAcctEnabledRegions.length === 0) {
      this.awsAcctRegionsError = 'Enable at least one region.';
      return;
    }
    if (!this.awsAcctEnabledRegions.includes(this.awsAcctDefaultRegion)) {
      this.awsAcctRegionsError = 'The default region must be one of the enabled regions.';
      return;
    }
    const accountId = this.awsEditRegionsAccountId;
    this.awsAcctRegionsSaving = true;
    this.awsAcctRegionsError  = null;
    this.aws.updateAccountRegions(accountId, this.awsAcctDefaultRegion, this.awsAcctEnabledRegions).subscribe({
      next: () => {
        this.awsAcctRegionsSaving    = false;
        this.awsEditRegionsAccountId = null;
        this.toast.show('Regions updated.', 'success');
        this.loadAwsConnection();
        this.cloud.refreshConnection();
      },
      error: (err: { error?: { message?: string } }) => {
        this.awsAcctRegionsSaving = false;
        this.awsAcctRegionsError  = err?.error?.message ?? 'Failed to update regions.';
      },
    });
  }

  // Region catalog + the primary account's region context (used by the
  // connection summary and the per-account geography editor seed). Regions are
  // edited per account via the `awsAcct*` state below, never connection-wide.
  readonly supportedRegions = SUPPORTED_AWS_REGIONS;
  awsDefaultRegion  = 'us-east-1';
  awsEnabledRegions: string[] = ['us-east-1'];

  awsTestLoading      = false;
  awsDisconnectLoading = false;

  awsTestError: string | null = null;
  awsTestSuccess = false;

  awsPolicyExpanded = false;
  awsCopySuccess    = false;

  isReadOnly = false;

  // ── Azure (service-principal connect) ─────────────────────────────────────

  azureState: AzurePageState = 'loading';
  azureConnection: AzureConnection | null = null;
  readonly supportedLocations = SUPPORTED_AZURE_LOCATIONS;
  readonly locationLabel = locationLabel;

  // Connect form inputs
  azureTenantId       = '';
  azureSubscriptionId = '';
  azureClientId       = '';
  azureClientSecret   = '';
  azureDisplayName    = '';
  azureDefaultLocation = 'eastus';

  azureTestLoading      = false;
  azureTestError: string | null = null;
  azureTestSuccess      = false;
  azureDisconnectLoading = false;

  // ── Per-subscription management (locations editor + verify + disconnect) ─────
  /** Subscription whose locations editor is currently expanded, or null. */
  azureEditLocationsSubId: string | null = null;
  azureLocEnabled: string[] = [];
  azureLocDefault  = 'eastus';
  azureLocSaving   = false;
  azureLocError: string | null = null;
  /** Subscription currently being re-verified, or null. */
  azureVerifyingSubId: string | null = null;
  /** Subscription currently being promoted to primary, or null. */
  azureMakingPrimarySubId: string | null = null;
  /** Subscription pending a disconnect confirmation, or null. */
  azureConfirmDisconnectSub: { subscriptionId: string; label?: string; isPrimary: boolean } | null = null;
  azureDisconnectSubLoading = false;

  // ── GCP (service-account connect) ─────────────────────────────────────────

  gcpState: GcpPageState = 'loading';
  gcpConnection: GcpConnection | null = null;
  readonly supportedGcpRegions = SUPPORTED_GCP_REGIONS;
  readonly regionLabel = regionLabel;

  // Connect form inputs
  gcpProjectId          = '';
  gcpServiceAccountKey  = '';   // pasted service account key JSON (raw string)
  gcpDisplayName        = '';
  gcpDefaultRegion      = 'us-central1';

  gcpTestLoading      = false;
  gcpTestError: string | null = null;
  gcpTestSuccess      = false;
  gcpDisconnectLoading = false;

  // ── Per-project management (regions editor + verify + disconnect) ────────────
  /** Project whose regions editor is currently expanded, or null. */
  gcpEditRegionsProjectId: string | null = null;
  gcpRegEnabled: string[] = [];
  gcpRegDefault  = 'us-central1';
  gcpRegSaving   = false;
  gcpRegError: string | null = null;
  /** Project currently being re-verified, or null. */
  gcpVerifyingProjectId: string | null = null;
  /** Project currently being promoted to primary, or null. */
  gcpMakingPrimaryProjectId: string | null = null;
  /** Project pending a disconnect confirmation, or null. */
  gcpConfirmDisconnectProject: { projectId: string; label?: string; isPrimary: boolean } | null = null;
  gcpDisconnectProjectLoading = false;

  // ── Email notification config ─────────────────────────────────────────────

  emailConfig: EmailChannelState | null = null;
  emailLoadState: 'loading' | 'loaded' | 'error' = 'loading';
  emailSaveState: 'idle' | 'saving' = 'idle';
  emailRemoveState: 'idle' | 'removing' = 'idle';
  emailDraftIds: string[] = [];
  emailDraftEnabled = true;
  emailShowForm = false;
  orgMembers: MemberWithUser[] = [];

  constructor(
    private readonly jira:         JiraService,
    private readonly github:       GitHubService,
    private readonly aws:          AwsService,
    private readonly azure:        AzureService,
    private readonly gcp:          GcpService,
    private readonly route:        ActivatedRoute,
    private readonly orgService:   OrgService,
    private readonly notification: NotificationService,
    private readonly toast:        ToastService,
    private readonly cloud:        CloudService,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;

    // Jira callback params
    if (params.get('connected') === 'true') {
      this.jiraConnectSuccess = true;
    } else if (params.get('error')) {
      this.jiraConnectError = this.friendlyJiraError(params.get('error')!);
    }

    // GitHub callback params — prefixed to avoid collision with Jira's params
    if (params.get('github_connected') === 'true') {
      this.githubConnectSuccess = true;
    } else if (params.get('github_error')) {
      this.githubConnectError = this.friendlyGitHubError(params.get('github_error')!);
    }

    this.orgService.getMyOrgs().pipe(take(1)).subscribe(({ memberships }) => {
      if (memberships.length > 0) {
        this.isReadOnly = memberships[0].membership.role === 'member';
        const orgId = memberships[0].org.orgId;
        this.orgService.listMembers(orgId).pipe(take(1)).subscribe(({ members }) => {
          this.orgMembers = members.filter(m => m.membershipStatus === 'active');
        });
      }
    });

    this.loadJiraConnection();
    this.loadGitHubConnection();
    this.loadAwsConnection();
    this.loadAzureConnection();
    this.loadGcpConnection();
    this.loadEmailConfig();
  }

  // ── Email notification actions ─────────────────────────────────────────────

  private loadEmailConfig(): void {
    this.emailLoadState = 'loading';
    this.notification.getEmailConfig().subscribe({
      next: (state) => {
        this.emailConfig    = state;
        this.emailLoadState = 'loaded';
      },
      error: () => { this.emailLoadState = 'error'; },
    });
  }

  startConfigureEmail(): void {
    this.emailDraftIds     = this.emailConfig?.recipientIds ?? [];
    this.emailDraftEnabled = this.emailConfig?.enabled ?? true;
    this.emailShowForm     = true;
  }

  cancelEmailForm(): void {
    this.emailShowForm = false;
    this.emailDraftIds = [];
  }

  saveEmailConfig(): void {
    if (this.emailDraftEnabled && this.emailDraftIds.length === 0) {
      this.toast.show('At least one recipient is required', 'error');
      return;
    }
    this.emailSaveState = 'saving';
    this.notification.saveEmailConfig(this.emailDraftEnabled, this.emailDraftIds).subscribe({
      next: (saved) => {
        this.emailConfig    = saved;
        this.emailSaveState = 'idle';
        this.emailShowForm  = false;
        this.toast.show('Email notification recipients updated.', 'success');
      },
      error: (err: { error?: { message?: string } }) => {
        this.emailSaveState = 'idle';
        this.toast.show(err?.error?.message ?? 'Failed to save email config. Please try again.', 'error');
      },
    });
  }

  toggleEmailEnabled(): void {
    if (!this.emailConfig || this.emailSaveState === 'saving') return;
    const newEnabled = !this.emailConfig.enabled;
    if (newEnabled && this.emailConfig.recipientIds.length === 0) {
      this.toast.show('Add recipients before enabling email notifications.', 'error');
      return;
    }
    this.emailSaveState = 'saving';
    this.notification.saveEmailConfig(newEnabled, this.emailConfig.recipientIds).subscribe({
      next: (saved) => {
        this.emailConfig    = saved;
        this.emailSaveState = 'idle';
        this.toast.show(`Email notifications ${newEnabled ? 'enabled' : 'paused'}.`, 'success');
      },
      error: (err: { error?: { message?: string } }) => {
        this.emailSaveState = 'idle';
        this.toast.show(err?.error?.message ?? 'Failed to update email config.', 'error');
      },
    });
  }

  removeEmailConfig(): void {
    this.emailRemoveState = 'removing';
    this.notification.removeEmailConfig().subscribe({
      next: () => {
        this.emailConfig      = null;
        this.emailRemoveState = 'idle';
        this.emailShowForm    = false;
        this.toast.show('Email notification config removed.', 'success');
      },
      error: () => {
        this.emailRemoveState = 'idle';
        this.toast.show('Failed to remove email config.', 'error');
      },
    });
  }

  memberDisplayName(m: MemberWithUser): string {
    const full = `${m.givenName} ${m.familyName}`.trim();
    return full || m.email;
  }

  emailRecipientNames(): string {
    if (!this.emailConfig || this.emailConfig.recipientIds.length === 0) return '';
    const names = this.emailConfig.recipientIds.map(id => {
      const m = this.orgMembers.find(member => member.userId === id);
      return m ? this.memberDisplayName(m) : id;
    });
    return names.join(', ');
  }

  // ── Jira actions ──────────────────────────────────────────────────────────

  private loadJiraConnection(): void {
    this.jiraState = 'loading';
    this.jira.getConnection().subscribe({
      next: (conn) => {
        this.jiraConnection = conn;
        if (!conn) {
          this.jiraState = 'not-connected';
        } else if (conn.status === 'PENDING_SITE_SELECTION') {
          this.pendingSites = conn.pendingSites ?? [];
          this.jiraState = 'pending-site';
        } else if (conn.status === 'NEEDS_REAUTH') {
          this.jiraState = 'needs-reauth';
        } else {
          this.jiraState = 'connected';
          this.loadJiraDefaults();
        }
      },
      error: () => { this.jiraState = 'error'; },
    });
  }

  private loadJiraDefaults(): void {
    this.jira.getDefaults().subscribe({
      next: (defaults) => {
        this.jiraDefaults    = defaults;
        this.jiraDefaultDraft = defaults.projectKey ?? '';
      },
      error: () => { /* non-fatal — defaults to unconfigured state */ },
    });
  }

  saveJiraDefaults(): void {
    const key = this.jiraDefaultDraft.trim().toUpperCase();
    if (!key) { this.jiraDefaultError = 'Project key is required'; return; }
    if (!/^[A-Z][A-Z0-9]{0,9}$/.test(key)) {
      this.jiraDefaultError = 'Enter a valid Jira project key (e.g. OPS, INFRA)';
      return;
    }

    this.jiraDefaultError   = null;
    this.jiraDefaultSuccess = false;
    this.jiraDefaultSaving  = true;

    this.jira.saveDefaults(key).subscribe({
      next: (saved) => {
        this.jiraDefaults       = saved;
        this.jiraDefaultDraft   = saved.projectKey ?? key;
        this.jiraDefaultSaving  = false;
        this.jiraDefaultSuccess = true;
        setTimeout(() => { this.jiraDefaultSuccess = false; }, 3000);
      },
      error: () => {
        this.jiraDefaultError  = 'Failed to save. Please try again.';
        this.jiraDefaultSaving = false;
      },
    });
  }

  connectJira(): void {
    this.jiraConnectLoading = true;
    this.jiraConnectError = null;
    this.jira.startConnect().subscribe({
      next: ({ authUrl }) => { window.location.href = authUrl; },
      error: () => {
        this.jiraConnectError = 'Failed to start Jira connection. Please try again.';
        this.jiraConnectLoading = false;
      },
    });
  }

  selectSite(cloudId: string): void {
    this.selectingCloudId = cloudId;
    this.siteSelectError = null;
    this.jira.selectSite(cloudId).subscribe({
      next: (conn) => {
        this.jiraConnection = conn;
        this.jiraState = 'connected';
        this.jiraConnectSuccess = true;
        this.selectingCloudId = null;
        this.pendingSites = [];
      },
      error: (err: { error?: { message?: string } }) => {
        this.siteSelectError = err?.error?.message ?? 'Failed to select site. Please try again.';
        this.selectingCloudId = null;
      },
    });
  }

  disconnectJira(): void {
    this.jiraDisconnectLoading = true;
    this.jira.disconnect().subscribe({
      next: () => {
        this.jiraConnection = null;
        this.jiraState = 'not-connected';
        this.jiraConnectSuccess = false;
        this.jiraDisconnectLoading = false;
      },
      error: () => { this.jiraDisconnectLoading = false; },
    });
  }

  private friendlyJiraError(code: string): string {
    const map: Record<string, string> = {
      access_denied:   'You declined the Jira authorization request.',
      invalid_state:   'The authorization request expired or was already used. Please try again.',
      exchange_failed: 'Failed to exchange the authorization code. Please try again.',
      no_sites:        'Your Atlassian account has no accessible Jira sites.',
      missing_params:  'The callback was missing required parameters. Please try again.',
    };
    return map[code] ?? 'Jira connection failed. Please try again.';
  }

  // ── GitHub actions ────────────────────────────────────────────────────────

  private loadGitHubConnection(): void {
    this.githubState = 'loading';
    this.github.getConnection().subscribe({
      next: (conn) => {
        this.githubConnection = conn;
        this.githubState = conn ? 'connected' : 'not-connected';
        if (conn) this.loadGitHubDefaults();
      },
      error: () => { this.githubState = 'error'; },
    });
  }

  private loadGitHubDefaults(): void {
    this.github.getDefaults().subscribe({
      next: (defaults) => {
        this.githubDefaults    = defaults;
        this.githubDefaultDraft = defaults.repository ?? '';
      },
      error: () => { /* non-fatal — defaults to unconfigured state */ },
    });
  }

  saveGitHubDefaults(): void {
    const repo = this.githubDefaultDraft.trim();
    if (!repo) { this.githubDefaultError = 'Repository is required'; return; }
    if (!/^[a-zA-Z0-9_.-]+\/[a-zA-Z0-9_.-]+$/.test(repo)) {
      this.githubDefaultError = 'Use the format owner/repository (e.g. myorg/my-repo)';
      return;
    }

    this.githubDefaultError   = null;
    this.githubDefaultSuccess = false;
    this.githubDefaultSaving  = true;

    this.github.saveDefaults(repo).subscribe({
      next: (saved) => {
        this.githubDefaults       = saved;
        this.githubDefaultDraft   = saved.repository ?? repo;
        this.githubDefaultSaving  = false;
        this.githubDefaultSuccess = true;
        setTimeout(() => { this.githubDefaultSuccess = false; }, 3000);
      },
      error: () => {
        this.githubDefaultError  = 'Failed to save. Please try again.';
        this.githubDefaultSaving = false;
      },
    });
  }

  connectGitHub(): void {
    this.githubConnectLoading = true;
    this.githubConnectError = null;
    this.github.startConnect().subscribe({
      next: ({ authUrl }) => { window.location.href = authUrl; },
      error: () => {
        this.githubConnectError = 'Failed to start GitHub connection. Please try again.';
        this.githubConnectLoading = false;
      },
    });
  }

  disconnectGitHub(): void {
    this.githubDisconnectLoading = true;
    this.github.disconnect().subscribe({
      next: () => {
        this.githubConnection = null;
        this.githubState = 'not-connected';
        this.githubConnectSuccess = false;
        this.githubDisconnectLoading = false;
      },
      error: () => { this.githubDisconnectLoading = false; },
    });
  }

  private friendlyGitHubError(code: string): string {
    const map: Record<string, string> = {
      access_denied:      'You declined the GitHub authorization request.',
      invalid_state:      'The authorization request expired or was already used. Please try again.',
      exchange_failed:    'Failed to exchange the authorization code. Please try again.',
      user_fetch_failed:  'Connected to GitHub but could not retrieve your profile. Please try again.',
      missing_params:     'The callback was missing required parameters. Please try again.',
    };
    return map[code] ?? 'GitHub connection failed. Please try again.';
  }

  // ── AWS actions ───────────────────────────────────────────────────────────

  private loadAwsConnection(): void {
    this.awsState = 'loading';
    this.aws.getConnection().subscribe({
      next: (conn) => {
        this.awsConnection = conn;
        if (conn) {
          this.awsEnabledRegions = (conn.enabledRegions && conn.enabledRegions.length)
            ? [...conn.enabledRegions]
            : ['us-east-1'];
          this.awsDefaultRegion = conn.defaultRegion ?? this.awsEnabledRegions[0];
        }
        if (!conn || conn.connectionStatus !== 'ACTIVE') {
          // Pre-populate the form with any previously saved role ARN / region
          if (conn?.roleArn)       this.awsRoleArn = conn.roleArn;
          if (conn?.defaultRegion) this.awsRegion  = conn.defaultRegion;
          this.awsState = 'not-connected';
        } else {
          this.awsState = 'connected';
        }
      },
      error: () => { this.awsState = 'error'; },
    });
  }

  testConnectAws(): void {
    this.awsTestLoading = true;
    this.awsTestError   = null;
    this.awsTestSuccess = false;
    this.awsState       = 'testing';

    this.aws.testConnection(this.awsRoleArn, this.awsRegion).subscribe({
      next: (result) => {
        this.awsTestLoading = false;
        if (result.success) {
          this.awsTestSuccess = true;
          this.awsState       = 'connected';
          // Reload to get the full connection record (with connectedAt etc.)
          this.loadAwsConnection();
        } else {
          this.awsTestError = result.message;
          this.awsState     = 'not-connected';
        }
      },
      error: (err: { error?: { message?: string } }) => {
        this.awsTestLoading = false;
        this.awsTestError   = err?.error?.message ?? 'Failed to test connection. Please try again.';
        this.awsState       = 'not-connected';
      },
    });
  }

  get awsTrustPolicyExample(): string {
    const accountId  = this.awsConnection?.kernalHqAccountId ?? 'KERNAL_HQ_ACCOUNT_ID';
    const externalId = this.awsConnection?.externalId ?? 'YOUR_EXTERNAL_ID';
    return `{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Principal": { "AWS": "arn:aws:iam::${accountId}:root" },
    "Action": "sts:AssumeRole",
    "Condition": { "StringEquals": { "sts:ExternalId": "${externalId}" } }
  }]
}`;
  }

  get awsPermissionsPolicyExample(): string {
    return `{
  "Version": "2012-10-17",
  "Statement": [{
    "Sid": "KernalHQRead",
    "Effect": "Allow",
    "Action": [
      "lambda:ListFunctions",
      "lambda:GetFunction",
      "lambda:GetFunctionConfiguration",
      "glue:GetJobs",
      "glue:GetJobRuns",
      "codepipeline:ListPipelines",
      "codepipeline:GetPipelineState",
      "codepipeline:ListPipelineExecutions",
      "states:ListStateMachines",
      "states:DescribeStateMachine",
      "states:ListExecutions",
      "states:DescribeExecution",
      "ecs:ListClusters",
      "ecs:ListServices",
      "ecs:DescribeServices",
      "rds:DescribeDBInstances",
      "rds:DescribeEvents",
      "apigateway:GET",
      "ec2:DescribeInstances",
      "cognito-idp:ListUserPools",
      "appsync:ListGraphqlApis",
      "dynamodb:ListTables",
      "dynamodb:DescribeTable",
      "sqs:ListQueues",
      "sqs:GetQueueAttributes",
      "events:ListEventBuses",
      "events:ListRules",
      "elasticloadbalancing:DescribeLoadBalancers",
      "sns:ListTopics",
      "sns:GetTopicAttributes",
      "cloudfront:ListDistributions",
      "cloudwatch:GetMetricData",
      "logs:StartQuery",
      "logs:StopQuery",
      "logs:GetQueryResults",
      "logs:GetLogGroupFields"
    ],
    "Resource": "*"
  }]
}`;
  }

  copyPolicy(): void {
    navigator.clipboard.writeText(this.awsPermissionsPolicyExample).then(() => {
      this.awsCopySuccess = true;
      setTimeout(() => { this.awsCopySuccess = false; }, 2000);
    });
  }

  disconnectAws(): void {
    this.awsDisconnectLoading = true;
    this.aws.disconnect().subscribe({
      next: () => {
        this.awsConnection        = null;
        this.awsRoleArn           = '';
        this.awsRegion            = 'us-east-1';
        this.awsTestSuccess       = false;
        this.awsTestError         = null;
        this.awsDisconnectLoading = false;
        // Reload to re-generate the externalId record so user can reconfigure
        this.loadAwsConnection();
      },
      error: () => { this.awsDisconnectLoading = false; },
    });
  }

  // ── Azure actions ─────────────────────────────────────────────────────────

  private loadAzureConnection(): void {
    this.azureState = 'loading';
    this.azure.getConnection().subscribe({
      next: (conn) => {
        this.azureConnection = conn;
        const active = !!conn && conn.subscriptionCount > 0 && conn.connectionStatus === 'ACTIVE';
        this.azureState = active ? 'connected' : 'not-connected';
        // Pre-fill the tenant id when a service principal is already configured.
        if (conn?.tenantId) this.azureTenantId = conn.tenantId;
      },
      error: () => { this.azureState = 'error'; },
    });
  }

  /** Connected Azure subscriptions (drives the connected-state summary). */
  get azureSubscriptions(): AzureSubscription[] {
    return this.azureConnection?.subscriptions ?? [];
  }

  /** Stable identity for the Azure subscriptions *ngFor (mirrors AWS trackBy). */
  trackByAzureSubscriptionId(_index: number, s: AzureSubscription): string {
    return s.subscriptionId;
  }

  /**
   * Validates + connects an Azure subscription via a service principal. Mirrors
   * `testConnectAws`: a failure surfaces an inline error and keeps the form
   * visible; success flips to the connected state and reloads the integration.
   */
  testConnectAzure(): void {
    if (this.isReadOnly) return;
    const tenantId       = this.azureTenantId.trim();
    const subscriptionId = this.azureSubscriptionId.trim();
    const clientId       = this.azureClientId.trim();
    const clientSecret   = this.azureClientSecret;
    if (!tenantId || !subscriptionId || !clientId || !clientSecret) {
      this.azureTestError = 'Tenant ID, Subscription ID, Client ID and Client secret are all required.';
      return;
    }

    this.azureTestLoading = true;
    this.azureTestError   = null;
    this.azureTestSuccess = false;
    this.azureState       = 'testing';

    this.azure.testConnection({
      tenantId, clientId, clientSecret, subscriptionId,
      displayName: this.azureDisplayName.trim() || undefined,
      defaultLocation: this.azureDefaultLocation,
      enabledLocations: [this.azureDefaultLocation],
    }).subscribe({
      next: (result) => {
        this.azureTestLoading = false;
        if (result.success && result.connectionStatus === 'ACTIVE') {
          this.azureTestSuccess  = true;
          this.azureClientSecret = ''; // never retain the secret in the form
          this.azureSubscriptionId = '';
          this.azureDisplayName    = '';
          this.toast.show('Azure subscription connected.', 'success');
          this.loadAzureConnection();
        } else {
          this.azureTestError = 'Azure connection failed. Check the service principal and try again.';
          this.azureState     = 'not-connected';
        }
      },
      error: (err: { error?: { message?: string } }) => {
        this.azureTestLoading = false;
        this.azureTestError   = err?.error?.message ?? 'Failed to connect Azure. Please try again.';
        this.azureState       = 'not-connected';
      },
    });
  }

  disconnectAzure(): void {
    if (this.isReadOnly || this.azureDisconnectLoading) return;
    this.azureDisconnectLoading = true;
    this.azure.disconnect().subscribe({
      next: () => {
        this.azureDisconnectLoading = false;
        this.azureConnection        = null;
        this.azureTestSuccess       = false;
        this.azureTestError         = null;
        this.toast.show('Azure integration disconnected.', 'success');
        this.loadAzureConnection();
      },
      error: (err: { error?: { message?: string } }) => {
        this.azureDisconnectLoading = false;
        this.toast.show(err?.error?.message ?? 'Failed to disconnect Azure.', 'error');
      },
    });
  }

  // ── Per-subscription: locations editor ──────────────────────────────────────

  /** True when more than one subscription is connected (drives affordances/copy). */
  get hasMultipleAzureSubscriptions(): boolean {
    return this.azureSubscriptions.length > 1;
  }

  /** The primary subscription's id, for the connection-details summary. */
  get azurePrimarySubscriptionId(): string | null {
    return this.azureConnection?.primarySubscriptionId ?? null;
  }

  /** Expands the locations editor for one subscription, seeded with its current locations. */
  startEditAzureLocations(sub: AzureSubscription): void {
    if (this.isReadOnly) return;
    const enabled = sub.enabledLocations?.length ? [...sub.enabledLocations] : [];
    const def = sub.defaultLocation ?? enabled[0] ?? 'eastus';
    this.azureEditLocationsSubId = sub.subscriptionId;
    this.azureLocEnabled = enabled.length ? enabled : [def];
    this.azureLocDefault = def;
    this.azureLocError   = null;
  }

  cancelEditAzureLocations(): void {
    this.azureEditLocationsSubId = null;
    this.azureLocError = null;
  }

  /** Toggles a location for the subscription being edited (the default stays enabled). */
  toggleAzureLocation(code: string): void {
    if (this.azureLocEnabled.includes(code)) {
      if (code === this.azureLocDefault) return; // can't disable the default location
      this.azureLocEnabled = this.azureLocEnabled.filter(l => l !== code);
    } else {
      this.azureLocEnabled = [...this.azureLocEnabled, code];
    }
    this.azureLocError = null;
  }

  isAzureLocationEnabled(code: string): boolean {
    return this.azureLocEnabled.includes(code);
  }

  /** Persists the locations for ONE subscription; leaves the others untouched. */
  saveAzureLocations(): void {
    if (this.isReadOnly || !this.azureEditLocationsSubId) return;
    if (this.azureLocEnabled.length === 0) {
      this.azureLocError = 'Enable at least one location.';
      return;
    }
    if (!this.azureLocEnabled.includes(this.azureLocDefault)) {
      this.azureLocError = 'The default location must be one of the enabled locations.';
      return;
    }
    const subscriptionId = this.azureEditLocationsSubId;
    this.azureLocSaving = true;
    this.azureLocError  = null;
    this.azure.updateSubscriptionLocations(subscriptionId, this.azureLocDefault, this.azureLocEnabled).subscribe({
      next: () => {
        this.azureLocSaving = false;
        this.azureEditLocationsSubId = null;
        this.toast.show('Locations updated.', 'success');
        this.loadAzureConnection();
        this.cloud.refreshConnection();
      },
      error: (err: { error?: { message?: string } }) => {
        this.azureLocSaving = false;
        this.azureLocError  = err?.error?.message ?? 'Failed to update locations.';
      },
    });
  }

  // ── Per-subscription: re-verify ─────────────────────────────────────────────

  /** Re-checks a subscription against Azure using the stored service principal. */
  verifyAzureSubscription(sub: AzureSubscription): void {
    if (this.isReadOnly || this.azureVerifyingSubId) return;
    this.azureVerifyingSubId = sub.subscriptionId;
    this.azure.verifySubscription(sub.subscriptionId).subscribe({
      next: (result) => {
        this.azureVerifyingSubId = null;
        if (result.success) {
          this.toast.show(`Subscription ${result.subscriptionId} verified.`, 'success');
        } else {
          this.toast.show(result.message ?? `Subscription ${result.subscriptionId} could not be verified.`, 'error');
        }
        this.loadAzureConnection();
        this.cloud.refreshConnection();
      },
      error: (err: { error?: { message?: string } }) => {
        this.azureVerifyingSubId = null;
        this.toast.show(err?.error?.message ?? 'Failed to verify the subscription.', 'error');
      },
    });
  }

  // ── Per-subscription: make primary ──────────────────────────────────────────

  /** Promotes an ACTIVE subscription to primary (manual, not just auto-promotion). */
  makePrimaryAzureSubscription(sub: AzureSubscription): void {
    if (this.isReadOnly || this.azureMakingPrimarySubId) return;
    if (sub.isPrimary) return;
    this.azureMakingPrimarySubId = sub.subscriptionId;
    this.azure.setPrimarySubscription(sub.subscriptionId).subscribe({
      next: () => {
        this.azureMakingPrimarySubId = null;
        this.toast.show(`${sub.label || sub.subscriptionId} is now the primary subscription.`, 'success');
        this.loadAzureConnection();
        this.cloud.refreshConnection();
      },
      error: (err: { error?: { message?: string } }) => {
        this.azureMakingPrimarySubId = null;
        this.toast.show(err?.error?.message ?? 'Failed to set the primary subscription.', 'error');
      },
    });
  }

  // ── Per-subscription: disconnect ────────────────────────────────────────────

  askDisconnectAzureSubscription(sub: AzureSubscription): void {
    if (this.isReadOnly) return;
    this.azureConfirmDisconnectSub = { subscriptionId: sub.subscriptionId, label: sub.label, isPrimary: sub.isPrimary ?? false };
  }

  cancelDisconnectAzureSubscription(): void {
    this.azureConfirmDisconnectSub = null;
  }

  /** Confirms and disconnects a single subscription. Non-destructive to the others. */
  confirmDisconnectAzureSubscription(): void {
    if (this.isReadOnly || !this.azureConfirmDisconnectSub) return;
    if (this.azureDisconnectSubLoading) return; // guard against double-submit
    const { subscriptionId } = this.azureConfirmDisconnectSub;
    this.azureDisconnectSubLoading = true;
    this.azure.deleteSubscription(subscriptionId).subscribe({
      next: () => {
        this.azureDisconnectSubLoading = false;
        this.azureConfirmDisconnectSub = null;
        if (this.azureEditLocationsSubId === subscriptionId) this.azureEditLocationsSubId = null;
        this.toast.show(`Subscription ${subscriptionId} disconnected.`, 'success');
        this.loadAzureConnection();
        this.cloud.refreshConnection();
      },
      error: (err: { error?: { message?: string } }) => {
        this.azureDisconnectSubLoading = false;
        this.azureConfirmDisconnectSub = null;
        this.toast.show(err?.error?.message ?? 'Failed to disconnect the subscription.', 'error');
      },
    });
  }

  // ── GCP actions ───────────────────────────────────────────────────────────

  private loadGcpConnection(): void {
    this.gcpState = 'loading';
    this.gcp.getConnection().subscribe({
      next: (conn) => {
        this.gcpConnection = conn;
        const active = !!conn && conn.projectCount > 0 && conn.connectionStatus === 'ACTIVE';
        this.gcpState = active ? 'connected' : 'not-connected';
      },
      error: () => { this.gcpState = 'error'; },
    });
  }

  /** Connected GCP projects (drives the connected-state summary). */
  get gcpProjects(): GcpProject[] {
    return this.gcpConnection?.projects ?? [];
  }

  /** Stable identity for the GCP projects *ngFor (mirrors AWS/Azure trackBy). */
  trackByGcpProjectId(_index: number, p: GcpProject): string {
    return p.projectId;
  }

  /**
   * Validates + connects a GCP project via a service account key. Mirrors
   * `testConnectAzure`: a failure surfaces an inline error and keeps the form
   * visible; success flips to the connected state and reloads the integration.
   * The pasted key is submitted as `serviceAccountKey` — parsed to an object
   * when it is valid JSON, otherwise sent as the raw string.
   */
  testConnectGcp(): void {
    if (this.isReadOnly) return;
    const projectId = this.gcpProjectId.trim();
    const rawKey    = this.gcpServiceAccountKey.trim();
    if (!projectId || !rawKey) {
      this.gcpTestError = 'Project ID and the service account key JSON are both required.';
      return;
    }

    let serviceAccountKey: object | string = rawKey;
    try {
      serviceAccountKey = JSON.parse(rawKey);
    } catch {
      // Not valid JSON — submit the raw string; the backend validates it.
      serviceAccountKey = rawKey;
    }

    this.gcpTestLoading = true;
    this.gcpTestError   = null;
    this.gcpTestSuccess = false;
    this.gcpState       = 'testing';

    this.gcp.testConnection({
      serviceAccountKey, projectId,
      displayName: this.gcpDisplayName.trim() || undefined,
      defaultRegion: this.gcpDefaultRegion,
      enabledRegions: [this.gcpDefaultRegion],
    }).subscribe({
      next: (result) => {
        this.gcpTestLoading = false;
        if (result.success && result.connectionStatus === 'ACTIVE') {
          this.gcpTestSuccess       = true;
          this.gcpServiceAccountKey = ''; // never retain the key in the form
          this.gcpProjectId         = '';
          this.gcpDisplayName       = '';
          this.toast.show('Google Cloud project connected.', 'success');
          this.loadGcpConnection();
        } else {
          this.gcpTestError = 'Google Cloud connection failed. Check the service account and try again.';
          this.gcpState     = 'not-connected';
        }
      },
      error: (err: { error?: { message?: string } }) => {
        this.gcpTestLoading = false;
        this.gcpTestError   = err?.error?.message ?? 'Failed to connect Google Cloud. Please try again.';
        this.gcpState       = 'not-connected';
      },
    });
  }

  disconnectGcp(): void {
    if (this.isReadOnly || this.gcpDisconnectLoading) return;
    this.gcpDisconnectLoading = true;
    this.gcp.disconnect().subscribe({
      next: () => {
        this.gcpDisconnectLoading = false;
        this.gcpConnection        = null;
        this.gcpTestSuccess       = false;
        this.gcpTestError         = null;
        this.toast.show('Google Cloud integration disconnected.', 'success');
        this.loadGcpConnection();
      },
      error: (err: { error?: { message?: string } }) => {
        this.gcpDisconnectLoading = false;
        this.toast.show(err?.error?.message ?? 'Failed to disconnect Google Cloud.', 'error');
      },
    });
  }

  // ── Per-project: regions editor ─────────────────────────────────────────────

  /** True when more than one project is connected (drives affordances/copy). */
  get hasMultipleGcpProjects(): boolean {
    return this.gcpProjects.length > 1;
  }

  /** The primary project's id, for the connection-details summary. */
  get gcpPrimaryProjectId(): string | null {
    return this.gcpConnection?.primaryProjectId ?? null;
  }

  /** Expands the regions editor for one project, seeded with its current regions. */
  startEditGcpRegions(proj: GcpProject): void {
    if (this.isReadOnly) return;
    const enabled = proj.enabledRegions?.length ? [...proj.enabledRegions] : [];
    const def = proj.defaultRegion ?? enabled[0] ?? 'us-central1';
    this.gcpEditRegionsProjectId = proj.projectId;
    this.gcpRegEnabled = enabled.length ? enabled : [def];
    this.gcpRegDefault = def;
    this.gcpRegError   = null;
  }

  cancelEditGcpRegions(): void {
    this.gcpEditRegionsProjectId = null;
    this.gcpRegError = null;
  }

  /** Toggles a region for the project being edited (the default stays enabled). */
  toggleGcpRegion(code: string): void {
    if (this.gcpRegEnabled.includes(code)) {
      if (code === this.gcpRegDefault) return; // can't disable the default region
      this.gcpRegEnabled = this.gcpRegEnabled.filter(r => r !== code);
    } else {
      this.gcpRegEnabled = [...this.gcpRegEnabled, code];
    }
    this.gcpRegError = null;
  }

  isGcpRegionEnabled(code: string): boolean {
    return this.gcpRegEnabled.includes(code);
  }

  /** Persists the regions for ONE project; leaves the others untouched. */
  saveGcpRegions(): void {
    if (this.isReadOnly || !this.gcpEditRegionsProjectId) return;
    if (this.gcpRegEnabled.length === 0) {
      this.gcpRegError = 'Enable at least one region.';
      return;
    }
    if (!this.gcpRegEnabled.includes(this.gcpRegDefault)) {
      this.gcpRegError = 'The default region must be one of the enabled regions.';
      return;
    }
    const projectId = this.gcpEditRegionsProjectId;
    this.gcpRegSaving = true;
    this.gcpRegError  = null;
    this.gcp.updateProjectRegions(projectId, this.gcpRegDefault, this.gcpRegEnabled).subscribe({
      next: () => {
        this.gcpRegSaving = false;
        this.gcpEditRegionsProjectId = null;
        this.toast.show('Regions updated.', 'success');
        this.loadGcpConnection();
        this.cloud.refreshConnection();
      },
      error: (err: { error?: { message?: string } }) => {
        this.gcpRegSaving = false;
        this.gcpRegError  = err?.error?.message ?? 'Failed to update regions.';
      },
    });
  }

  // ── Per-project: re-verify ──────────────────────────────────────────────────

  /** Re-checks a project against Google Cloud using the stored service account. */
  verifyGcpProject(proj: GcpProject): void {
    if (this.isReadOnly || this.gcpVerifyingProjectId) return;
    this.gcpVerifyingProjectId = proj.projectId;
    this.gcp.verifyProject(proj.projectId).subscribe({
      next: (result) => {
        this.gcpVerifyingProjectId = null;
        if (result.success) {
          this.toast.show(`Project ${result.projectId} verified.`, 'success');
        } else {
          this.toast.show(result.message ?? `Project ${result.projectId} could not be verified.`, 'error');
        }
        this.loadGcpConnection();
        this.cloud.refreshConnection();
      },
      error: (err: { error?: { message?: string } }) => {
        this.gcpVerifyingProjectId = null;
        this.toast.show(err?.error?.message ?? 'Failed to verify the project.', 'error');
      },
    });
  }

  // ── Per-project: make primary ───────────────────────────────────────────────

  /** Promotes an ACTIVE project to primary (manual, not just auto-promotion). */
  makePrimaryGcpProject(proj: GcpProject): void {
    if (this.isReadOnly || this.gcpMakingPrimaryProjectId) return;
    if (proj.isPrimary) return;
    this.gcpMakingPrimaryProjectId = proj.projectId;
    this.gcp.setPrimaryProject(proj.projectId).subscribe({
      next: () => {
        this.gcpMakingPrimaryProjectId = null;
        this.toast.show(`${proj.label || proj.projectId} is now the primary project.`, 'success');
        this.loadGcpConnection();
        this.cloud.refreshConnection();
      },
      error: (err: { error?: { message?: string } }) => {
        this.gcpMakingPrimaryProjectId = null;
        this.toast.show(err?.error?.message ?? 'Failed to set the primary project.', 'error');
      },
    });
  }

  // ── Per-project: disconnect ─────────────────────────────────────────────────

  askDisconnectGcpProject(proj: GcpProject): void {
    if (this.isReadOnly) return;
    this.gcpConfirmDisconnectProject = { projectId: proj.projectId, label: proj.label, isPrimary: proj.isPrimary ?? false };
  }

  cancelDisconnectGcpProject(): void {
    this.gcpConfirmDisconnectProject = null;
  }

  /** Confirms and disconnects a single project. Non-destructive to the others. */
  confirmDisconnectGcpProject(): void {
    if (this.isReadOnly || !this.gcpConfirmDisconnectProject) return;
    if (this.gcpDisconnectProjectLoading) return; // guard against double-submit
    const { projectId } = this.gcpConfirmDisconnectProject;
    this.gcpDisconnectProjectLoading = true;
    this.gcp.deleteProject(projectId).subscribe({
      next: () => {
        this.gcpDisconnectProjectLoading = false;
        this.gcpConfirmDisconnectProject = null;
        if (this.gcpEditRegionsProjectId === projectId) this.gcpEditRegionsProjectId = null;
        this.toast.show(`Project ${projectId} disconnected.`, 'success');
        this.loadGcpConnection();
        this.cloud.refreshConnection();
      },
      error: (err: { error?: { message?: string } }) => {
        this.gcpDisconnectProjectLoading = false;
        this.gcpConfirmDisconnectProject = null;
        this.toast.show(err?.error?.message ?? 'Failed to disconnect the project.', 'error');
      },
    });
  }

  // ── Category helpers ──────────────────────────────────────────────────────

  comingSoonFor(category: IntegrationCategory): ComingSoonIntegration[] {
    return COMING_SOON.filter(i => i.category === category);
  }
}
